import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { FilterSettingsModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { AvpCustomAdaptor } from './../../../../base/shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { CreateEmailAccountSettingsDTO, UpdateEmailAccountSettingsDTO, EmailAccountDirection, EmailAccountSettingsDTO, EmailAccountType, NameAndIdDTO } from './../../../../base/models/models.swagger.generated';
import { EmailSettingsFormType } from '../email-settings-form-type';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmailSettingsService } from '../email-settings.service';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Component, Input, OnInit } from '@angular/core';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { EmailSettingsEditFormComponent } from '../email-settings-edit-form/email-settings-edit-form.component';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';

export type TypeEnumFilterType = {
  accountType: EmailAccountType;
}

@Component({
    selector: 'app-email-settings-grid',
    templateUrl: './email-settings-grid.component.html',
    styleUrls: ['./email-settings-grid.component.scss'],
    imports: [EmailSettingsEditFormComponent, GridModule, ToolbarModule, TranslateModule]
})
export class EmailSettingsGridComponent extends GridBaseComponent<EmailSettingsFormType> implements OnInit{

  @Input() public isEditModeEqualsEntry: boolean;

  public filterOptions: FilterSettingsModel;
  public EmailAccountType = EmailAccountType;

  public typeFilter = GridConstantsFactory.createCheckBoxFilter();

  constructor(
    private fb: FormBuilder,
    private emailSettingsService: EmailSettingsService,
    translateService: TranslateService,
    private spinnerService: SpinnerService) {
      super(translateService, emailSettingsService);
  }

  public ngOnInit() {
    //Filter for the two seperate grids
    this.filterOptions = {
      columns: [{ field: 'direction', matchCase: false, operator: 'equal', predicate: 'and', value: this.isEditModeEqualsEntry ? EmailAccountDirection.In : EmailAccountDirection.Out }]
    };

    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.emailSettingsService, this.spinnerService)
    });

    this.isActiveColumnName = 'accountName';
    this.editDialogTitle = 'i18n.domain.settings.email-settings.edit-title';
    this.addIndexFindColumnName = 'accountName';
  }

  public addConfiguration(): void{
    if(this.isEditModeEqualsEntry) {
      this.addDialogTitle = 'i18n.domain.settings.email-settings.add-title-in';
    } else {
      this.addDialogTitle = 'i18n.domain.settings.email-settings.add-title-out';
    }
    this.onAdd();
  }

  public formatFilterType(typeEnumFilter: TypeEnumFilterType): string {
    if (!typeEnumFilter || typeEnumFilter.accountType == null) {
      return '';
    }
    return this.translateService.instant('i18n.shared.email-account-type.' + EmailAccountType[typeEnumFilter.accountType]);
  }

  public onRowDataBound(args: any) {
    const data = args.data as EmailAccountSettingsDTO;
    if (data.importConfigurationInfos.length > 0 || data.exportInterfaceConfigurationInfos.length > 0 || data.exportInterfaceConfigurationTemplateInfos.length > 0 || data.exportInterfaceActivationEmailTemplateInfos.length > 0) {
      var buttons = args.row.cells[args.row.cells.length - 1].getElementsByClassName('e-deletebutton');
      if (buttons) {
        buttons[0].style.visibility = 'hidden';
      }
    }
  }

  protected override getDataToSave(): CreateEmailAccountSettingsDTO | UpdateEmailAccountSettingsDTO {
    const form = this.gridEditForm as FormGroup<EmailSettingsFormType>;
    const formValues = form.value;
    if (!formValues.id) {
      return this.emailSettingsService.getCreateEmailAccountSettingsDTOFromForm(form);
    } else {
      return new UpdateEmailAccountSettingsDTO({
        id: formValues.id,
        accountName:  formValues.accountName,
        accountType:  formValues.accountType,
        direction: formValues.direction,
        receiveOnlyMailsWithAttachments: formValues.receiveOnlyMailsWithAttachments,
        emailAddress: formValues.emailAddress,
        password:  formValues.password,
        serverUrl: formValues.serverUrl,
        userName: formValues.userName,
        useSSL: formValues.useSSL,
        port: formValues.port,
        azureClientId: formValues.azureClientId,
        azureClientSecret: formValues.azureClientSecret,
        azureTenantId: formValues.azureTenantId,
        archiveFolderName: formValues.archiveFolderName,
      });
    }
  }

  protected initializeEditForm(data: EmailAccountSettingsDTO): void {
    this.gridEditForm = this.fb.group<EmailSettingsFormType>({
      id: this.fb.control<number>(data.id),
      accountName: this.fb.control<string>(data.accountName, [Validators.required]),
      accountType: this.fb.control<EmailAccountType>((data.accountType != null && data.accountType != undefined) ? data.accountType : EmailAccountType.Office365, [Validators.required]),
      direction: this.fb.control<EmailAccountDirection>((data.direction ?? (this.isEditModeEqualsEntry ? EmailAccountDirection.In : EmailAccountDirection.Out)), [Validators.required]),
      receiveOnlyMailsWithAttachments: this.fb.control<boolean>(data.receiveOnlyMailsWithAttachments ?? false),
      emailAddress: this.fb.control<string>(data.emailAddress, [Validators.required, CustomValidatorsFactory.emailValidation()]),
      password: this.fb.control<string>(data.password, [Validators.required]),
      serverUrl: this.fb.control<string>(data.serverUrl),
      userName: this.fb.control<string>(data.userName),
      useSSL: this.fb.control<boolean>(data.useSSL ?? true),
      port: this.fb.control<number>(data.port),
      azureClientId: this.fb.control<string>(data.azureClientId),
      azureClientSecret: this.fb.control<string>(data.azureClientSecret),
      azureTenantId: this.fb.control<string>(data.azureTenantId),
      archiveFolderName: this.fb.control<string>(data.archiveFolderName),
      importConfigurationInfos: this.fb.control<NameAndIdDTO[]>(data.importConfigurationInfos ?? []),
      exportInterfaceConfigurationInfos: this.fb.control<NameAndIdDTO[]>(data.exportInterfaceConfigurationInfos ?? []),
      exportInterfaceConfigurationTemplateInfos: this.fb.control<NameAndIdDTO[]>(data.exportInterfaceConfigurationTemplateInfos ?? []),
      exportInterfaceActivationEmailTemplateInfos: this.fb.control<NameAndIdDTO[]>(data.exportInterfaceActivationEmailTemplateInfos ?? []),
    });
  }

  protected getEditDialogTitleParams(rowData: any) {
    return {param: rowData.accountName};
  }
}
